import React from "react";
import {
  FaRegFileAlt,
  FaCog,
  FaBell,
  FaSignOutAlt,
  FaCar,
  FaBug,
  FaLocationArrow,
} from "react-icons/fa";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import useStore from "../store/useStore";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useStore((state) => state.userData);

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
  };

  const LogOutFunc = async (path) => {
    try {
      useStore.getState().clearSessionContext();
      await signOut(auth);
      console.log("successfully signout.");
      navigate(path, { replace: true });
    } catch (error) {
      console.log("I got some error", error);
    }
  };

  const shopName =
    userData?.companyName ||
    userData?.shopName ||
    (userData?.firstName ? `${userData.firstName}'s Workshop` : "Americar Partner");

  return (
    <aside className="w-80 bg-white border-r border-slate-100 shadow-[0_0_24px_rgba(0,0,0,0.03)] px-6 py-7 flex flex-col h-screen fixed left-0 top-0 z-20">
      {/* Brand Logo (matches Landing Page) */}
      <div className="mb-6 px-1 select-none">
        <Link to="/dashboard" className="inline-block">
          <img
            src="/logo.png"
            alt="Americar"
            className="h-10 w-auto object-contain hover:opacity-90 transition-opacity"
            fetchPriority="high"
          />
        </Link>
      </div>

      {/* Workshop Profile Card */}
      <div className="mb-6 p-3 bg-slate-50 border border-slate-200/70 rounded-xl flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0 overflow-hidden text-[#007AFF] font-bold text-sm">
          {userData?.logoUrl || userData?.profilePictureUrl ? (
            <img
              src={userData.logoUrl || userData.profilePictureUrl}
              alt="Shop"
              className="w-full h-full object-cover"
            />
          ) : (
            (userData?.firstName?.[0] || "A").toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-800 truncate leading-tight">
            {shopName}
          </p>
          <p className="text-[11px] text-emerald-600 font-medium flex items-center gap-1 mt-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Verified Vendor
          </p>
        </div>
      </div>

      {/* Navigation Links with Section Headings */}
      <nav id="tour-sidebar-nav" className="flex flex-col flex-1 overflow-y-auto space-y-5 pr-1">
        {/* Main Section */}
        <div>
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Overview
          </p>
          <div className="space-y-1">
            {[
              { path: "/dashboard", label: "Dashboard", icon: FaCar },
              { path: "/branches", label: "Branches", icon: FaLocationArrow },
              { path: "/reports", label: "Reports", icon: FaRegFileAlt },
            ].map(({ path, label, icon: Icon }) => (
              <div
                key={path}
                id={`tour-link-${path.replace("/", "")}`}
                className={`flex items-center px-3.5 py-2.5 rounded-xl cursor-pointer transition-all text-sm group ${
                  isActive(path)
                    ? "bg-[#007AFF]/10 text-[#007AFF] font-semibold"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
                }`}
                onClick={() => handleNavigation(path)}
              >
                <Icon
                  className={`mr-3 w-4 h-4 transition-colors ${
                    isActive(path) ? "text-[#007AFF]" : "text-slate-400 group-hover:text-slate-600"
                  }`}
                />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Management Section */}
        <div>
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Management
          </p>
          <div className="space-y-1">
            {[
              { path: "/notifications", label: "Notifications", icon: FaBell },
              { path: "/settings", label: "Settings", icon: FaCog },
            ].map(({ path, label, icon: Icon }) => (
              <div
                key={path}
                id={`tour-link-${path.replace("/", "")}`}
                className={`flex items-center px-3.5 py-2.5 rounded-xl cursor-pointer transition-all text-sm group ${
                  isActive(path)
                    ? "bg-[#007AFF]/10 text-[#007AFF] font-semibold"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
                }`}
                onClick={() => handleNavigation(path)}
              >
                <Icon
                  className={`mr-3 w-4 h-4 transition-colors ${
                    isActive(path) ? "text-[#007AFF]" : "text-slate-400 group-hover:text-slate-600"
                  }`}
                />
                {label}
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-slate-100 pt-4 mt-auto space-y-1">
        <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Support
        </p>
        <div
          className={`flex items-center px-3.5 py-2.5 rounded-xl cursor-pointer transition-all text-sm group ${
            isActive("/ReportIssue")
              ? "bg-[#007AFF]/10 text-[#007AFF] font-semibold"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
          }`}
          onClick={() => {
            handleNavigation("/ReportIssue");
          }}
        >
          <FaBug
            className={`mr-3 w-4 h-4 transition-colors ${
              isActive("/ReportIssue") ? "text-[#007AFF]" : "text-slate-400 group-hover:text-slate-600"
            }`}
          />
          Report An Issue
        </div>
        <div
          className="flex items-center px-3.5 py-2.5 rounded-xl hover:bg-red-50 text-slate-600 hover:text-red-600 cursor-pointer transition-all text-sm font-medium group"
          onClick={() => LogOutFunc("/login")}
        >
          <FaSignOutAlt className="mr-3 w-4 h-4 text-slate-400 group-hover:text-red-600 transition-colors" />
          Log out
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
